import { useEffect, useState, useCallback } from "react";
import { UserPlus, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

const SEMAFORO = {
  verde: { bg: "bg-emerald-50", border: "border-emerald-200", txt: "text-emerald-700", barra: "bg-emerald-500", label: "Dentro del plan" },
  ambar: { bg: "bg-amber-50", border: "border-amber-200", txt: "text-amber-700", barra: "bg-amber-500", label: "Al 80% del cupo del mes" },
  rojo: { bg: "bg-red-50", border: "border-red-200", txt: "text-red-700", barra: "bg-red-500", label: "Cupo del mes agotado" },
};

const num = (v) => Number(v || 0).toLocaleString("es-MX");

/**
 * Altas de clientes del mes contra el cupo del plan de la entidad.
 * Usa el cupo EFECTIVO (`limite_mes`), que ya incluye el ajuste manual del super_admin:
 * límite personalizado permanente y/o cupo extra del periodo.
 */
export default function AltasPlanCard() {
  const [data, setData] = useState(null);
  const [cargado, setCargado] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: r } = await api.get("/facturacion/uso-creditos");
      setData(r);
    } catch {
      setData(null);
    } finally {
      setCargado(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!cargado || !data) return null;
  const c = data.creditos || {};
  if (!c.enforcement) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="altas-plan-card-libre">
        <div className="flex items-center gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-xl bg-slate-100">
            <UserPlus className="h-5 w-5 text-slate-600" />
          </div>
          <div>
            <div className="text-xs text-slate-500">Altas de clientes</div>
            <div className="text-base font-semibold text-slate-800">
              Sin límite mensual en tu contrato
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (c.unlimited) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="altas-plan-card-ilimitado">
        <div className="flex items-center gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-xl bg-slate-100">
            <UserPlus className="h-5 w-5 text-slate-600" />
          </div>
          <div>
            <div className="text-xs text-slate-500">Altas de clientes · mes {c.periodo}</div>
            <div className="text-base font-semibold text-slate-800" data-testid="altas-plan-numeros">
              Altas ilimitadas en tu plan
            </div>
          </div>
        </div>
      </div>
    );
  }

  const limite = Number(c.limite_mes || 0);
  const usados = Number(c.usados || 0);
  const pct = limite > 0 ? Math.round((usados / limite) * 100) : 0;
  const s = SEMAFORO[pct >= 100 ? "rojo" : pct >= 80 ? "ambar" : "verde"];
  const ajustado = c.limite_personalizado != null || Number(c.extra_mes || 0) > 0;

  return (
    <div className={`rounded-2xl border ${s.border} ${s.bg} p-4`} data-testid="altas-plan-card">
      <div className="flex items-start gap-3">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-white/60">
          {pct >= 100
            ? <AlertTriangle className={`h-5 w-5 ${s.txt}`} />
            : <UserPlus className={`h-5 w-5 ${s.txt}`} />}
        </div>
        <div className="flex-1">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-xs text-slate-500">Altas de clientes · mes {c.periodo}</div>
              <div className="text-lg font-semibold text-slate-800" data-testid="altas-plan-numeros">
                {num(usados)} de {num(limite)} altas del mes
                {Number(c.wallet || 0) > 0 && (
                  <span className="text-sm font-normal text-slate-600">
                    {" "}· Recarga adicional: {num(c.wallet)}
                  </span>
                )}
              </div>
            </div>
            <span className={`rounded-full bg-white/70 px-2 py-1 text-xs font-semibold ${s.txt}`} data-testid="altas-plan-semaforo">
              {s.label}
            </span>
          </div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white/70">
            <div className={`h-full ${s.barra}`} style={{ width: `${Math.min(100, pct)}%` }} />
          </div>
          <div className="mt-2 text-xs text-slate-600" data-testid="altas-plan-detalle">
            Quedan <b>{num(c.disponibles_incluidos)}</b> altas del cupo del mes
            {ajustado && (
              <span data-testid="altas-plan-ajuste">
                {" "}· Cupo ajustado por FELVORA
                {c.limite_personalizado != null && ` (límite propio de ${num(c.limite_personalizado)})`}
                {Number(c.extra_mes || 0) > 0 && ` (+${num(c.extra_mes)} solo este mes)`}
              </span>
            )}
            {data.clientes_activos != null && (
              <span> · {num(data.clientes_activos)} clientes activos en total</span>
            )}
          </div>
          {pct >= 100 && (
            <div className="mt-2 text-sm text-red-700">
              Se agotó el cupo de altas del mes. Solicita ampliación a FELVORA para seguir
              dando de alta clientes.
              {Number(c.precio_alta_adicional || 0) > 0
                && ` Cada alta adicional cuesta $${num(c.precio_alta_adicional)} MXN + IVA.`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
